import { ISODate } from "@/types/project";

export type Granularity = "month" | "quarter" | "week";

const MS_PER_DAY = 86400000;

export const clampUTC = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

export const isoKey = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;

export const overlapDaysInclusive = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): number => {
  const start = Math.max(clampUTC(aStart).getTime(), clampUTC(bStart).getTime());
  const end = Math.min(clampUTC(aEnd).getTime(), clampUTC(bEnd).getTime());
  if (end < start) return 0;
  return Math.floor((end - start) / MS_PER_DAY) + 1;
};

export const toDate = (v: ISODate | null): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const mStart = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
export const mEnd = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
export const addM = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));

export const qStart = (d: Date) => {
  const q = Math.floor(d.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(d.getUTCFullYear(), q, 1));
};
export const addQ = (d: Date, n: number) => {
  const m = d.getUTCMonth() + n * 3;
  const y = d.getUTCFullYear() + Math.floor(m / 12);
  const nm = ((m % 12) + 12) % 12;
  return new Date(Date.UTC(y, nm, 1));
};
export const qEnd = (d: Date) => {
  const s = qStart(d);
  const next = addQ(s, 1);
  return new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth(), 0));
};

export const wStart = (d: Date) => {
  const wd = (d.getUTCDay() + 6) % 7;
  const s = clampUTC(d);
  s.setUTCDate(s.getUTCDate() - wd);
  return s;
};
export const wEnd = (d: Date) => {
  const s = wStart(d);
  const e = new Date(s.getTime());
  e.setUTCDate(e.getUTCDate() + 6);
  return e;
};
export const addW = (d: Date, n: number) => {
  const s = wStart(d);
  const nd = new Date(s.getTime());
  nd.setUTCDate(nd.getUTCDate() + n * 7);
  return wStart(nd);
};

export type Period = { start: Date; end: Date; key: string };

export const buildPeriodsMonth = (start: Date, end: Date): Period[] => {
  const out: Period[] = [];
  for (let d = mStart(start); d <= mEnd(end); d = addM(d, 1)) {
    const ps = mStart(d),
      pe = mEnd(d);
    out.push({ start: ps, end: pe, key: isoKey(ps) });
  }
  return out;
};

export const buildPeriodsQuarter = (start: Date, end: Date): Period[] => {
  const out: Period[] = [];
  const S = qStart(start);
  const E = qEnd(end);
  for (let d = new Date(S); d <= E; d = addQ(d, 1)) {
    const ps = qStart(d),
      pe = qEnd(d);
    const key = isoKey(ps);
    out.push({ start: ps, end: pe, key });
  }
  return out;
};

export const buildPeriodsWeek = (start: Date, end: Date): Period[] => {
  const out: Period[] = [];
  for (let d = wStart(start); d <= wEnd(end); d = addW(d, 1)) {
    const ps = wStart(d),
      pe = wEnd(d);
    out.push({ start: ps, end: pe, key: isoKey(ps) });
  }
  return out;
};

export type Distribution = { start_date: string; end_date: string; daily_budget: number };

export const allocateByPeriods = (
  periods: Period[],
  dist: Distribution[]
): { per: Record<string, number>; total: number; minStart: Date | null; maxEnd: Date | null } => {
  const per: Record<string, number> = {};
  periods.forEach((p) => (per[p.key] = 0));

  let minStart: Date | null = null;
  let maxEnd: Date | null = null;
  let total = 0;

  for (const d of dist) {
    const s = toDate(d.start_date);
    const e = toDate(d.end_date);
    const daily = Number(d.daily_budget) || 0;
    if (!s || !e || daily === 0) continue;

    if (!minStart || s < minStart) minStart = s;
    if (!maxEnd || e > maxEnd) maxEnd = e;

    for (const p of periods) {
      const ndays = overlapDaysInclusive(s, e, p.start, p.end);
      if (ndays > 0) {
        const amount = ndays * daily;
        per[p.key] += amount;
        total += amount;
      }
    }
  }

  return { per, total, minStart, maxEnd };
};
